<script setup>
// QR / Barcode 生成器（nuxt#19 P3）。复刻 design generator.js：输入文本 → canvas 实时绘制 → 下载 PNG。
// 确定性绘制(hash 种子)使页面离线即用;生产级编码可后续换真库,UI 不变。
import { ref, computed, watch, onMounted, nextTick } from "vue";

const props = defineProps({ mode: { type: String, default: "qr" } }); // qr | barcode
const { downloadBlob } = useFreeToolsDownload();

const text = ref("");
const fg = ref("#0b0d13");
const canvas = ref(null);
const copied = ref(false);
const SWATCHES = ["#0b0d13", "#1f6feb", "#16a34a", "#dc2626", "#7c3aed"];
const placeholder = computed(() => (props.mode === "barcode" ? "Enter text or numbers to encode…" : "Enter a URL, text, WiFi or contact…"));
const dlName = computed(() => (props.mode === "barcode" ? "barcode" : "qr-code"));

function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function rng(seed) { let x = seed || 123456789; return () => { x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return (x >>> 0) / 4294967296; }; }

function drawQR(c, t) {
  const ctx = c.getContext("2d");
  const N = 29, q = 4, total = N + q * 2, S = Math.floor(360 / total) * total, cell = S / total;
  c.width = S; c.height = S;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, S, S);
  if (!t) return;
  const rand = rng(hashStr(t));
  ctx.fillStyle = fg.value;
  const on = (col, r) => ctx.fillRect((col + q) * cell, (r + q) * cell, cell, cell);
  const inFinder = (col, r, n) => (col < 8 && r < 8) || (col > n - 9 && r < 8) || (col < 8 && r > n - 9);
  for (let r = 0; r < N; r++) for (let col = 0; col < N; col++) { if (inFinder(col, r, N)) continue; if (rand() > 0.5) on(col, r); }
  const finder = (x, y) => { for (let i = 0; i < 7; i++) for (let j = 0; j < 7; j++) { const edge = i === 0 || i === 6 || j === 0 || j === 6, core = i >= 2 && i <= 4 && j >= 2 && j <= 4; if (edge || core) on(x + j, y + i); } };
  finder(0, 0); finder(N - 7, 0); finder(0, N - 7);
}
function drawBarcode(c, t) {
  const ctx = c.getContext("2d");
  const W = 480, H = 200, pad = 30; c.width = W; c.height = H;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
  if (!t) return;
  ctx.fillStyle = fg.value;
  const rand = rng(hashStr(t)); let x = pad; const top = 22, bh = H - 70;
  while (x < W - pad) { const bw = 1 + Math.floor(rand() * 4); if (rand() > 0.5) ctx.fillRect(x, top, bw, bh); x += bw + (rand() > 0.6 ? 2 : 1); }
  ctx.fillRect(pad, top, 2, bh + 8); ctx.fillRect(W - pad - 2, top, 2, bh + 8);
  ctx.font = '600 18px "Public Sans", monospace'; ctx.textAlign = "center";
  ctx.fillText(t.slice(0, 24), W / 2, H - 22);
}
function render() { const c = canvas.value; if (!c) return; const t = text.value.trim(); props.mode === "barcode" ? drawBarcode(c, t) : drawQR(c, t); }

watch([text, fg, () => props.mode], render);
onMounted(() => nextTick(render));

function download() {
  const c = canvas.value; if (!c) return;
  c.toBlob((b) => downloadBlob(b, dlName.value + ".png"), "image/png");
}
async function copyImg() {
  const c = canvas.value; if (!c) return;
  try { const b = await new Promise((r) => c.toBlob(r, "image/png")); await navigator.clipboard.write([new ClipboardItem({ "image/png": b })]); copied.value = true; setTimeout(() => (copied.value = false), 1400); } catch (_) {}
}
</script>

<template>
  <div class="gen">
    <div class="gen-stage">
      <div class="gen-preview"><canvas ref="canvas" class="gen-canvas" /></div>
      <div class="gen-panel">
        <div class="gen-group">
          <label>{{ mode === "barcode" ? "Barcode content" : "QR content" }}</label>
          <input v-model="text" type="text" :placeholder="placeholder" />
        </div>
        <div class="gen-group">
          <label>Color</label>
          <div class="sw-row">
            <button v-for="s in SWATCHES" :key="s" class="sw" :class="{ active: fg === s }" :style="{ background: s }" type="button" @click="fg = s" />
          </div>
        </div>
        <div class="gen-actions">
          <button class="btn btn-primary btn-lg" type="button" @click="download">Download PNG</button>
          <button class="btn btn-outline" type="button" @click="copyImg">{{ copied ? "Copied!" : "Copy image" }}</button>
        </div>
        <p class="gen-note">Generated right in your browser — never stored.</p>
      </div>
    </div>
    <div class="trust">
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>Runs in your browser</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>Instant</span>
      <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Free forever</span>
    </div>
  </div>
</template>

<style scoped>
.gen-stage { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; display: grid; grid-template-columns: 1fr 1fr; }
.gen-preview { display: grid; place-items: center; padding: 30px; background: var(--surface-2); min-height: 320px; }
.gen-canvas { max-width: 100%; height: auto; background: #fff; border-radius: 12px; box-shadow: 0 8px 30px -10px rgba(0, 0, 0, 0.5); }
.gen-panel { padding: 30px; border-left: 1px solid var(--line); display: flex; flex-direction: column; gap: 18px; }
.gen-group label { font-size: 12px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--tx-faint); display: block; margin-bottom: 10px; }
.gen-group input { width: 100%; background: var(--surface-2); border: 1px solid var(--line); border-radius: 11px; padding: 12px 14px; color: var(--tx); font-family: var(--font-b); font-size: 15px; outline: none; }
.gen-group input:focus { border-color: #f97316; }
.sw-row { display: flex; gap: 10px; }
.sw { width: 34px; height: 34px; border-radius: 9px; border: 2px solid var(--line); cursor: pointer; padding: 0; }
.sw.active { border-color: var(--tx); box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px #f97316; }
.gen-actions { display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
.gen-note { font-size: 12.5px; color: var(--tx-faint); }
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 18px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #f97316; }
@media (max-width: 760px) { .gen-stage { grid-template-columns: 1fr; } .gen-panel { border-left: none; border-top: 1px solid var(--line); } }
</style>
